import { Connection, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { getCurrentRPCEndpoints } from '../config/constants';

/**
 * Blockchain Utilities
 *
 * Standalone module for Solana blockchain interactions:
 * - RPC connection management with automatic fallback
 * - Balance fetching and formatting
 * - Transaction helpers
 *
 * This module can be easily modified or replaced without affecting
 * other parts of the application.
 */

let connectionInstance = null;
let currentEndpointIndex = 0;

/**
 * Get or create Solana connection with automatic fallback
 * If the current endpoint fails, it will try the next one
 */
export function getConnection() {
    if (!connectionInstance) {
        const endpoints = getCurrentRPCEndpoints();
        const endpoint = endpoints[currentEndpointIndex % endpoints.length];
        connectionInstance = new Connection(endpoint, 'confirmed');
    }
    return connectionInstance;
}

/**
 * Try next RPC endpoint (fallback mechanism)
 */
export function tryNextEndpoint() {
    currentEndpointIndex++;
    connectionInstance = null;
    return getConnection();
}

/**
 * Fetch SOL balance for a given public key
 *
 * @param {PublicKey} publicKey - The wallet's public key
 * @returns {Promise<number>} Balance in SOL (not lamports)
 */
export async function getSOLBalance(publicKey) {
    if (!publicKey) {
        return 0;
    }

    try {
        const connection = getConnection();
        const balance = await connection.getBalance(publicKey);
        return balance / LAMPORTS_PER_SOL;
    } catch (error) {
        console.error('Error fetching SOL balance:', error);

        // Try fallback endpoint
        try {
            const connection = tryNextEndpoint();
            const balance = await connection.getBalance(publicKey);
            return balance / LAMPORTS_PER_SOL;
        } catch (fallbackError) {
            console.error('Fallback endpoint also failed:', fallbackError);
            return 0;
        }
    }
}

/**
 * Format SOL amount to display string
 *
 * @param {number} amount - Amount in SOL
 * @param {number} decimals - Number of decimal places (default: 4)
 * @returns {string} Formatted string
 */
export function formatSOL(amount, decimals = 4) {
    if (typeof amount !== 'number' || isNaN(amount)) {
        return '0.0000';
    }
    return amount.toFixed(decimals);
}

/**
 * Convert SOL to lamports
 *
 * @param {number} sol - Amount in SOL
 * @returns {number} Amount in lamports
 */
export function solToLamports(sol) {
    return Math.floor(sol * LAMPORTS_PER_SOL);
}

/**
 * Convert lamports to SOL
 *
 * @param {number} lamports - Amount in lamports
 * @returns {number} Amount in SOL
 */
export function lamportsToSol(lamports) {
    return lamports / LAMPORTS_PER_SOL;
}

/**
 * Check if wallet has sufficient balance
 *
 * @param {number} currentBalance - Current balance in SOL
 * @param {number} requiredAmount - Required amount in SOL
 * @returns {boolean} True if balance is sufficient
 */
export function hasSufficientBalance(currentBalance, requiredAmount) {
    return currentBalance >= requiredAmount;
}

/**
 * Get recent block hash (used for transactions)
 */
export async function getRecentBlockhash() {
    try {
        const connection = getConnection();
        const { blockhash } = await connection.getLatestBlockhash();
        return blockhash;
    } catch (error) {
        console.error('Error fetching recent blockhash:', error);
        throw error;
    }
}
